import { Fragment, useEffect, useState } from "react";
import {
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Row,
  Spinner,
} from "reactstrap";
import axios from "axios";
import moment from "moment";
import { useNavigate, useParams } from "react-router";
import { ArrowLeft, Edit2 } from "react-feather";
import { toast } from "react-toastify";
import { baseURL } from "../../../../Services/api/baseURL";
import { H3, H5 } from "../../../../AbstractElements";
import useRbac from "../../../../Hooks/useRbac";
const FLASH_DEAL_API = `${baseURL}/api/flash-deals`;

function ViewFlashDeal() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { can } = useRbac();

  const [deal, setDeal] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDeal();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchDeal = async () => {
    setLoading(true);
    try {
      const token = JSON.parse(localStorage.getItem("token"));

      const response = await axios.get(`${FLASH_DEAL_API}/get-flash-deal/${id}`, {
        headers: { Authorization: token },
      });

      if (response.data.success) {
        setDeal(response.data.data);
      } else {
        toast.error("Unable to load flash deal");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "Flash deal not found");
    } finally {
      setLoading(false);
    }
  };

  const getLiveStatus = () => {
    if (!deal) return null;
    const now = new Date();
    const start = new Date(deal.startDate);
    const end = new Date(deal.endDate);

    if (now < start) return { text: "Upcoming", color: "warning" };
    if (now > end) return { text: "Expired", color: "dark" };
    return { text: "Running", color: "success" };
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "60vh" }}>
        <Spinner color="primary" />
      </div>
    );
  }

  if (!deal) {
    return (
      <Container fluid style={{ paddingTop: 30 }}>
        <div className="text-center py-5">
          <H5>Flash deal not found</H5>
          <Button color="primary" className="mt-3" onClick={() => navigate("/flash-deals")}>
            Back to Flash Deals
          </Button>
        </div>
      </Container>
    );
  }

  const live = getLiveStatus();

  return (
    <Fragment>
      <Container fluid>
        <div className="page-title">
          <Row className="align-items-center">
            <Col xs="6" className="d-flex align-items-center">
              <Button
                color="link"
                className="p-0 me-2"
                onClick={() => navigate("/flash-deals")}
                title="Back"
              >
                <ArrowLeft size={20} />
              </Button>
              <H3 className="mb-0">{deal.title}</H3>
            </Col>

            <Col xs="6" className="text-end">
              <Badge color={deal.status === "active" ? "success" : "secondary"} pill className="me-2 px-3">
                {deal.status}
              </Badge>
              {live && (
                <Badge color={live.color} pill className="me-2 px-3">
                  {live.text}
                </Badge>
              )}
              {can("items.update") && (
                <Button color="primary" onClick={() => navigate(`/flash-deals/edit/${deal._id}`)}>
                  <Edit2 size={14} className="me-1" />
                  Edit Deal
                </Button>
              )}
            </Col>
          </Row>
        </div>

        <Row>
          {/* Left: Details */}
          <Col lg="8">
            <Card>
              <CardBody>
                <H5 className="mb-3">Deal Details</H5>

                <Row className="mb-3">
                  <Col md={6}>
                    <div className="text-muted" style={{ fontSize: 12 }}>Subtitle</div>
                    <div>{deal.subtitle || "—"}</div>
                  </Col>
                  <Col md={6}>
                    <div className="text-muted" style={{ fontSize: 12 }}>Badge</div>
                    <div>{deal.badge || "—"}</div>
                  </Col>
                </Row>

                <Row className="mb-3">
                  <Col md={12}>
                    <div className="text-muted" style={{ fontSize: 12 }}>Description</div>
                    <div>{deal.description || "—"}</div>
                  </Col>
                </Row>

                <Row className="mb-3">
                  <Col md={6}>
                    <div className="text-muted" style={{ fontSize: 12 }}>Product</div>
                    <div>{deal.product?.productName || "—"}</div>
                  </Col>
                  <Col md={6}>
                    <div className="text-muted" style={{ fontSize: 12 }}>Variant</div>
                    <div>{deal.variant?.variantName || "—"}</div>
                  </Col>
                </Row>

                <Row className="mb-3">
                  <Col md={6}>
                    <div className="text-muted" style={{ fontSize: 12 }}>Original Price</div>
                    <div style={{ textDecoration: "line-through", opacity: 0.7 }}>
                      ${Number(deal.originalPrice ?? 0).toFixed(2)}
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="text-muted" style={{ fontSize: 12 }}>Deal Price</div>
                    <div style={{ fontWeight: 700, color: deal.buttonColor || "#d3178a" }}>
                      ${Number(deal.dealPrice ?? 0).toFixed(2)}
                    </div>
                  </Col>
                </Row>

                <Row className="mb-3">
                  <Col md={6}>
                    <div className="text-muted" style={{ fontSize: 12 }}>Start Date</div>
                    <div>{moment(deal.startDate).format("DD MMM YYYY, h:mm A")}</div>
                  </Col>
                  <Col md={6}>
                    <div className="text-muted" style={{ fontSize: 12 }}>End Date</div>
                    <div>{moment(deal.endDate).format("DD MMM YYYY, h:mm A")}</div>
                  </Col>
                </Row>

                <Row className="mb-3">
                  <Col md={6}>
                    <div className="text-muted" style={{ fontSize: 12 }}>Priority</div>
                    <div>{deal.priority ?? 0}</div>
                  </Col>
                  <Col md={6}>
                    <div className="text-muted" style={{ fontSize: 12 }}>Created</div>
                    <div>{moment(deal.createdAt).format("DD MMM YYYY, h:mm A")}</div>
                  </Col>
                </Row>
              </CardBody>
            </Card>

            {/* Banners */}
            <Card className="mt-3">
              <CardBody>
                <H5 className="mb-3">Banners</H5>
                <Row>
                  <Col md={6}>
                    <div className="text-muted mb-2" style={{ fontSize: 12 }}>Website Banner (11:1)</div>
                    {deal.websiteBanner ? (
                      <img
                        src={deal.websiteBanner}
                        alt="Website Banner"
                        style={{
                          width: "100%",
                          aspectRatio: "11/1",
                          objectFit: "cover",
                          borderRadius: 8,
                          border: "1px solid #eee",
                        }}
                      />
                    ) : (
                      <div className="text-muted py-4 text-center border rounded">No website banner</div>
                    )}
                  </Col>
                  <Col md={6}>
                    <div className="text-muted mb-2" style={{ fontSize: 12 }}>Mobile Banner (16:9)</div>
                    {deal.mobileBanner ? (
                      <img
                        src={deal.mobileBanner}
                        alt="Mobile Banner"
                        style={{
                          width: "100%",
                          aspectRatio: "16/9",
                          objectFit: "cover",
                          borderRadius: 8,
                          border: "1px solid #eee",
                        }}
                      />
                    ) : (
                      <div className="text-muted py-4 text-center border rounded">No mobile banner</div>
                    )}
                  </Col>
                </Row>
              </CardBody>
            </Card>
          </Col>

          {/* Right: Live preview, matching create/edit form's preview */}
          <Col lg="4">
            <Card>
              <CardBody>
                <H5 className="mb-3">Card Preview</H5>
                <div
                  style={{
                    background: deal.backgroundColor || "#1a1a1a",
                    color: deal.textColor || "#ffffff",
                    borderRadius: 16,
                    padding: 20,
                  }}
                >
                  {deal.badge && (
                    <div
                      style={{
                        fontSize: 11,
                        fontWeight: 700,
                        letterSpacing: 1,
                        color: deal.buttonColor || "#d3178a",
                        marginBottom: 8,
                      }}
                    >
                      {deal.badge}
                    </div>
                  )}
                  <div style={{ fontWeight: 700, fontSize: 18, lineHeight: 1.3 }}>
                    {deal.title}
                  </div>
                  <div style={{ fontSize: 13, opacity: 0.8, marginTop: 6 }}>
                    {deal.subtitle || deal.variant?.variantName}
                  </div>
                  <div style={{ marginTop: 14, display: "flex", alignItems: "baseline", gap: 8 }}>
                    <span style={{ textDecoration: "line-through", opacity: 0.5, fontSize: 14 }}>
                      ${Number(deal.originalPrice ?? 0).toFixed(2)}
                    </span>
                    <span style={{ fontSize: 24, fontWeight: 800, color: deal.buttonColor || "#d3178a" }}>
                      ${Number(deal.dealPrice ?? 0).toFixed(2)}
                    </span>
                  </div>
                  <button
                    type="button"
                    disabled
                    style={{
                      marginTop: 16,
                      width: "100%",
                      border: "none",
                      borderRadius: 999,
                      padding: "10px 16px",
                      background: deal.buttonColor || "#d3178a",
                      color: "#fff",
                      fontWeight: 600,
                    }}
                  >
                    Add to cart
                  </button>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </Fragment>
  );
}

export default ViewFlashDeal;